import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { content } from "../../content/content";
import { centers } from "../../data/centers";
import { fadeUp } from "../../hooks/useScrollReveal";
import { JourneyPath } from "../Ornaments";

const EASE = [0.22, 1, 0.36, 1] as const;

export function Centers() {
  const [activeId, setActiveId] = useState<string>(centers[0]?.id ?? "");
  const active = centers.find((c) => c.id === activeId) ?? centers[0];
  const others = centers.filter((c) => c.id !== activeId);

  const statusLabel = (status: (typeof centers)[number]["status"]) =>
    status === "flagship"
      ? content.centers.flagshipLabel
      : content.centers.newLabel;

  return (
    <section
      id="centers"
      className="paper-grain relative overflow-hidden bg-warm-cream/55 py-28 md:py-40"
    >
      <JourneyPath className="pointer-events-none absolute inset-0 z-0 h-full w-full text-indigo-block/30" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          className="mb-12 max-w-2xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p
            data-edit-id="content.centers.eyebrow"
            className="mb-4 font-body text-xs font-medium tracking-[0.22em] text-saffron-dark uppercase"
          >
            {content.centers.eyebrow}
          </p>
          <h2
            data-edit-id="content.centers.headline"
            className="text-balance font-display text-[clamp(2rem,4vw,3.5rem)] text-ink"
          >
            {content.centers.headline}
          </h2>
          <p
            data-edit-id="content.centers.intro"
            className="mt-4 font-body text-lg text-ink-muted"
          >
            {content.centers.intro}
          </p>
        </motion.div>

        <motion.div
          className="mb-8 flex flex-wrap gap-3"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {centers.map((c) => {
            const isActive = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveId(c.id)}
                aria-pressed={isActive}
                className={`flex items-center gap-3 rounded-2xl border px-5 py-3 font-body text-sm font-medium transition-colors duration-300 ${
                  isActive
                    ? "border-saffron-dark bg-saffron text-warm-white"
                    : "border-ink/12 bg-warm-white/70 text-ink-light hover:border-ink/30 hover:text-ink"
                }`}
              >
                <span
                  className={`text-[10px] font-medium tracking-[0.22em] uppercase ${
                    isActive ? "text-warm-white/80" : "text-saffron-dark"
                  }`}
                >
                  {statusLabel(c.status)}
                </span>
                <span aria-hidden="true" className="opacity-40">
                  ·
                </span>
                <span data-edit-id={`data.centers[${c.id}].short`}>
                  {c.short}
                </span>
              </button>
            );
          })}
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              <motion.article
                key={active.id}
                data-edit-id={`data.centers[${active.id}]`}
                className="overflow-hidden rounded-3xl border border-ink/10 bg-warm-white"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-warm-cream">
                  <img
                    data-edit-id={`data.centers[${active.id}].image`}
                    src={active.image}
                    alt={active.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <span
                    className={`absolute top-4 left-4 rounded-xl px-3 py-1.5 font-body text-[10px] font-medium tracking-[0.22em] uppercase backdrop-blur-sm ${
                      active.status === "flagship"
                        ? "bg-warm-white/85 text-saffron-dark"
                        : "bg-violet/90 text-warm-white"
                    }`}
                  >
                    {statusLabel(active.status)} · {active.since}
                  </span>
                </div>

                <div className="p-7 md:p-10">
                  <h3
                    data-edit-id={`data.centers[${active.id}].name`}
                    className="font-display text-[clamp(1.4rem,2.4vw,2rem)] leading-[1.15] text-ink"
                  >
                    {active.name}
                  </h3>
                  <p
                    data-edit-id={`data.centers[${active.id}].location`}
                    className="mt-1 font-body text-sm text-ink-muted"
                  >
                    {active.location}
                  </p>
                  <p
                    data-edit-id={`data.centers[${active.id}].description`}
                    className="mt-5 max-w-2xl font-body text-base leading-relaxed text-ink-light md:text-lg"
                  >
                    {active.description}
                  </p>

                  <div className="mt-7 grid gap-4 sm:grid-cols-3">
                    {active.stats.map((s) => (
                      <div
                        key={s.label}
                        data-edit-id={`data.centers[${active.id}].stats[${s.label}]`}
                        className="rounded-2xl bg-warm-cream/60 p-4 md:p-5"
                      >
                        <p className="font-display text-xl text-ink md:text-2xl">
                          {s.value}
                        </p>
                        <p className="mt-1 font-body text-[11px] font-medium tracking-[0.2em] text-ink-muted uppercase">
                          {s.label}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-7 border-t border-ink/8 pt-6">
                    <p
                      data-edit-id="content.centers.coursesHeading"
                      className="mb-5 font-body text-[11px] font-medium tracking-[0.22em] text-saffron-dark uppercase"
                    >
                      {content.centers.coursesHeading}
                    </p>
                    <div
                      data-edit-id={`data.centers[${active.id}].courses`}
                      className="grid gap-5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-x-6 sm:gap-y-6"
                    >
                      {active.courses.map((track) => (
                        <div
                          key={track.id}
                          data-edit-id={`data.centers[${active.id}].courses[${track.id}]`}
                          className="contents"
                        >
                          <p
                            data-edit-id={`data.centers[${active.id}].courses[${track.id}].label`}
                            className="font-body text-[10px] font-medium tracking-[0.22em] text-ink-muted uppercase sm:pt-1.5"
                          >
                            {track.label}
                          </p>
                          <ul className="flex flex-wrap gap-2">
                            {track.items.map((c, i) => (
                              <li
                                key={c}
                                data-edit-id={`data.centers[${active.id}].courses[${track.id}].items[${i}]`}
                                className="rounded-xl bg-warm-cream/70 px-3 py-1.5 font-body text-sm text-ink-light"
                              >
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>

          <div className="flex flex-col gap-4 lg:gap-6">
            {others.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveId(c.id)}
                data-edit-id={`data.centers[${c.id}]`}
                className="group flex flex-col overflow-hidden rounded-3xl border border-ink/10 bg-warm-white text-left transition-colors duration-300 hover:border-saffron-dark"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-warm-cream">
                  <img
                    data-edit-id={`data.centers[${c.id}].image`}
                    src={c.image}
                    alt={c.name}
                    className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 rounded-xl bg-warm-white/85 px-2.5 py-1 font-body text-[10px] font-medium tracking-[0.22em] text-saffron-dark uppercase backdrop-blur-sm">
                    {statusLabel(c.status)} · {c.since}
                  </span>
                </div>
                <div className="px-5 py-5 md:px-6 md:py-6">
                  <h3
                    data-edit-id={`data.centers[${c.id}].name`}
                    className="font-display text-lg text-ink md:text-xl"
                  >
                    {c.name}
                  </h3>
                  <p
                    data-edit-id={`data.centers[${c.id}].location`}
                    className="mt-1 font-body text-sm text-ink-muted"
                  >
                    {c.location}
                  </p>
                  <p
                    data-edit-id={`data.centers[${c.id}].tagline`}
                    className="mt-3 font-body text-sm leading-relaxed text-ink-light"
                  >
                    {c.tagline}
                  </p>
                  <p className="mt-4 inline-flex items-center gap-2 font-body text-xs font-medium tracking-[0.18em] text-saffron-dark uppercase transition-opacity group-hover:opacity-70">
                    {content.centers.switchPrefix} {c.short}
                    <span aria-hidden="true">→</span>
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
